(() => {
  const ROOT_SELECTOR = "[data-nc-shop-map]";
  const CARD_SELECTOR = ".shop-col";
  const DEFAULT_CENTER = [36.2048, 138.2529];
  const DEFAULT_ZOOM = 5;
  const PREFECTURES = [
    "北海道","青森県","岩手県","宮城県","秋田県","山形県","福島県",
    "茨城県","栃木県","群馬県","埼玉県","千葉県","東京都","神奈川県",
    "新潟県","富山県","石川県","福井県","山梨県","長野県",
    "岐阜県","静岡県","愛知県","三重県",
    "滋賀県","京都府","大阪府","兵庫県","奈良県","和歌山県",
    "鳥取県","島根県","岡山県","広島県","山口県",
    "徳島県","香川県","愛媛県","高知県",
    "福岡県","佐賀県","長崎県","熊本県","大分県","宮崎県","鹿児島県","沖縄県","海外"
  ];

  const normalize = (value) => String(value || "")
    .toLowerCase()
    .replace(/[！-～]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
    .replace(/\s+/g, "");

  const escapeHtml = (value) => String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

  const unique = (items) => [...new Set(items.filter(Boolean))].sort((a, b) => a.localeCompare(b, "ja"));
  const hasCoords = (store) => Number.isFinite(store.lat) && Number.isFinite(store.lng);
  const toCoordinate = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return null;
    const number = Number(raw);
    return Number.isFinite(number) ? number : null;
  };

  const inferPrefecture = (text) => PREFECTURES.find((prefecture) => text.includes(prefecture)) || "";
  const fallbackKey = (name, address) => normalize(`${name}|${address || ""}`);

  const extractAddress = (text) => {
    const lines = String(text || "").split("\n").map((line) => line.trim()).filter(Boolean);
    const addressLine = lines.find((line) => /^(〒|[0-9０-９]{3}[-−ーｰ]?[0-9０-９]{4}|10\/F|[A-Za-z0-9].*Hong Kong)/.test(line) || inferPrefecture(line));
    return addressLine
      ? addressLine.replace(/TEL\s*[:：]?\s*[+0-9０-９()\-\sｰー−]+/i, "").trim()
      : "";
  };

  const extractTel = (text) => {
    const match = String(text || "").match(/(?:TEL|電話番号)\s*[:：]?\s*([+0-9０-９()\-\sｰー−]+)/i);
    return match
      ? match[1]
        .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
        .replace(/[ｰー−]/g, "-")
        .replace(/\s+/g, " ")
        .trim()
      : "";
  };

  const cardRegion = (card) => {
    const section = card.closest(".shopify-section");
    return section?.querySelector(".section-header__title")?.textContent?.trim().replace("地⽅", "地方") || "";
  };

  const dedupeKey = (store) => normalize([store.name, store.address, store.lat, store.lng].join("|"));

  const fetchFallbackStores = async (root) => {
    const url = root.dataset.ncShopMapStoresUrl;
    if (!url) return new Map();

    try {
      const response = await fetch(url, { credentials: "same-origin" });
      if (!response.ok) return new Map();
      const data = await response.json();
      if (!Array.isArray(data)) return new Map();

      const stores = new Map();
      data.forEach((store) => {
        const nameAddressKey = fallbackKey(store.name, store.address);
        const nameKey = fallbackKey(store.name, "");
        if (!stores.has(nameAddressKey)) stores.set(nameAddressKey, store);
        if (!stores.has(nameKey)) stores.set(nameKey, store);
      });
      return stores;
    } catch (_error) {
      return new Map();
    }
  };

  const collectStores = (fallbackStores) => {
    const seen = new Set();
    return [...document.querySelectorAll(CARD_SELECTOR)].map((card, index) => {
    const name = card.dataset.ncShopName || card.querySelector(".shoppage-shoptitle, h4:not(.todohuken)")?.textContent?.trim() || "";
    const text = card.innerText || "";
    const address = extractAddress(text);
    const fallback = fallbackStores.get(fallbackKey(name, address)) || fallbackStores.get(fallbackKey(name, ""));
    const region = cardRegion(card);
    const category = card.dataset.ncShopCategory || card.querySelector(".shop-icon:not(.shop-icon--new):not(.shop-icon--headspa)")?.textContent?.trim() || fallback?.category || "ショップ";
    const lat = toCoordinate(card.dataset.ncShopLat) ?? (Number.isFinite(fallback?.lat) ? fallback.lat : null);
    const lng = toCoordinate(card.dataset.ncShopLng) ?? (Number.isFinite(fallback?.lng) ? fallback.lng : null);
    const id = card.id || `nc-shop-map-store-${index + 1}`;
    if (!card.id) card.id = id;

    return {
      id,
      order: index,
      card,
      name,
      category,
      region: region || fallback?.region || "",
      prefecture: inferPrefecture(`${address}\n${text}`) || fallback?.prefecture || (region === "海外" ? "海外" : ""),
      address,
      tel: extractTel(text),
      lat,
      lng,
      target: `#${CSS.escape(id)}`
    };
    }).filter((store) => {
      if (!store.name) return false;
      const key = dedupeKey(store);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const fillSelect = (select, values, label) => {
    select.innerHTML = [`<option value="">${label}</option>`]
      .concat(values.map((value) => `<option value="${escapeHtml(value)}">${escapeHtml(value)}</option>`))
      .join("");
  };

  const popupHtml = (store) => {
    const address = store.address ? `<p>${escapeHtml(store.address)}</p>` : "";
    const tel = store.tel ? `<p>TEL: ${escapeHtml(store.tel)}</p>` : "";
    return `
      <div class="nc-shop-map-popup">
        <strong>${escapeHtml(store.name)}</strong>
        <p>${escapeHtml(store.category)}${store.prefecture ? ` / ${escapeHtml(store.prefecture)}` : ""}</p>
        ${address}
        ${tel}
        <a href="#${escapeHtml(store.id)}" data-nc-shop-map-jump="${escapeHtml(store.id)}">詳細を見る</a>
      </div>
    `;
  };

  const init = async (root) => {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const els = {
      open: root.querySelector("[data-nc-shop-map-open]"),
      dialog: root.querySelector("[data-nc-shop-map-dialog]"),
      close: root.querySelector("[data-nc-shop-map-close]"),
      keyword: root.querySelector("[data-nc-shop-map-keyword]"),
      prefecture: root.querySelector("[data-nc-shop-map-prefecture]"),
      category: root.querySelector("[data-nc-shop-map-category]"),
      clear: root.querySelector("[data-nc-shop-map-clear]"),
      status: root.querySelector("[data-nc-shop-map-status]"),
      canvas: root.querySelector("[data-nc-shop-map-canvas]"),
      empty: root.querySelector("[data-nc-shop-map-empty]")
    };

    const fallbackStores = await fetchFallbackStores(root);
    const stores = collectStores(fallbackStores);
    const markers = new Map();
    let currentStores = stores;
    let map = null;
    let mapReady = false;

    const setActiveCard = (store) => {
      document.querySelectorAll(".shop-col.is-nc-map-target").forEach((card) => card.classList.remove("is-nc-map-target"));
      store.card.classList.add("is-nc-map-target");
    };

    const scrollToStore = (store) => {
      setActiveCard(store);
      store.card.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const renderStatus = () => {
      const mapped = currentStores.filter(hasCoords).length;
      const unmapped = currentStores.length - mapped;
      els.status.textContent = `検索結果 ${currentStores.length}件 / 地図表示 ${mapped}件 / 地図未設定 ${unmapped}件`;
      els.empty.hidden = Boolean(mapped);
    };

    const renderMap = () => {
      if (!map || !window.L) {
        renderStatus();
        return;
      }

      markers.forEach((marker) => marker.remove());
      markers.clear();

      const mappedStores = currentStores.filter(hasCoords);
      mappedStores.forEach((store) => {
        const marker = window.L.marker([store.lat, store.lng]).addTo(map).bindPopup(popupHtml(store));
        marker.on("click", () => setActiveCard(store));
        markers.set(store.id, marker);
      });

      renderStatus();

      if (!mappedStores.length) {
        map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      } else if (mappedStores.length === 1) {
        map.setView([mappedStores[0].lat, mappedStores[0].lng], 15);
        markers.get(mappedStores[0].id)?.openPopup();
      } else {
        map.fitBounds(window.L.latLngBounds(mappedStores.map((store) => [store.lat, store.lng])), {
          padding: [28, 28],
          maxZoom: 15
        });
      }
    };

    const ensureMap = () => {
      if (mapReady || !window.L) return;

      map = window.L.map(els.canvas, { scrollWheelZoom: false }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);

      mapReady = true;
      renderMap();
    };

    const openDialog = () => {
      els.dialog.hidden = false;
      document.body.classList.add("nc-shop-map-is-open");

      if (!window.L) {
        els.status.textContent = "地図ライブラリを読み込めませんでした。";
        els.empty.hidden = false;
        return;
      }

      ensureMap();
      window.setTimeout(() => {
        map?.invalidateSize();
        renderMap();
        els.keyword?.focus({ preventScroll: true });
      }, 80);
    };

    const closeDialog = (restoreFocus = true) => {
      els.dialog.hidden = true;
      document.body.classList.remove("nc-shop-map-is-open");
      if (restoreFocus) els.open?.focus({ preventScroll: true });
    };

    const applyFilters = () => {
      const keyword = normalize(els.keyword.value);
      const prefecture = els.prefecture.value;
      const category = els.category.value;

      currentStores = stores.filter((store) => {
        const target = normalize([store.name, store.category, store.region, store.prefecture, store.address, store.tel, store.card.innerText].join(" "));
        return (!keyword || target.includes(keyword)) &&
          (!prefecture || store.prefecture === prefecture) &&
          (!category || store.category === category);
      });

      renderMap();
    };

    fillSelect(els.prefecture, PREFECTURES.filter((prefecture) => stores.some((store) => store.prefecture === prefecture)), "すべての都道府県");
    fillSelect(els.category, unique(stores.map((store) => store.category)), "すべてのカテゴリ");

    if (!stores.length) {
      els.status.textContent = "ショップブロックが見つかりませんでした。";
      els.empty.hidden = false;
    }

    [els.keyword, els.prefecture, els.category].forEach((el) => {
      el.addEventListener("input", applyFilters);
      el.addEventListener("change", applyFilters);
    });

    els.clear.addEventListener("click", () => {
      els.keyword.value = "";
      els.prefecture.value = "";
      els.category.value = "";
      applyFilters();
    });

    els.canvas.addEventListener("click", (event) => {
      const link = event.target.closest("[data-nc-shop-map-jump]");
      if (!link) return;
      const store = stores.find((item) => item.id === link.dataset.ncShopMapJump);
      if (!store) return;
      event.preventDefault();
      closeDialog(false);
      window.setTimeout(() => scrollToStore(store), 80);
    });

    els.open.addEventListener("click", openDialog);
    els.close.addEventListener("click", closeDialog);
    els.dialog.addEventListener("click", (event) => {
      if (event.target === els.dialog) closeDialog();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !els.dialog.hidden) closeDialog();
    });

    renderStatus();
  };

  window.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  });
})();
